import React, {
  FC,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_URL, API_BASE_URL } from '../config';
import VoiceMessage from './VoiceMessage';
import type {
  User,
  VideoItem,
  Message,
  AppNotification,
  FloatingReaction,
  ActivityItem,
  FullscreenBanner,
  ReactionToast,
  MessageReactions,
  TabType,
  Party,
  PlaybackSyncPayload,
  MessageReactionPayload,
  FloatingReactionPayload,
  MessageSeenPayload,
  MessageEditedPayload,
} from '../types';
import './VideoRoom.scss';

// ── Constants ─────────────────────────────────────────────────────────────────
const DEFAULT_REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '🎉', '👏'] as const;
const REACTION_EMOJI_LIMIT = 10;
const REACTION_EMOJI_CATALOG = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
  '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥸', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️',
  '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓',
  '🤗', '🤔', '🫣', '🤭', '🫢', '🫡', '🤫', '🫠', '🤥', '😶', '😐', '😑', '😬', '🫥', '😯', '😦', '😧', '😮', '😲', '🥱',
  '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏', '🙌', '🙏', '🤝', '👊', '✊', '🫂',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝',
  '🔥', '✨', '⭐', '🌟', '💫', '⚡', '💥', '💯',
  '🎉', '🎊', '🎈', '🎁', '🎂', '🍰', '🍕', '🍔', '🍟', '🍩', '🍪', '🍫',
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '✈️', '🚀',
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷',
  '🌍', '🌎', '🌏', '🌙', '🌞', '⭐', '☁️', '⛅', '🌈', '⚡', '❄️', '🔥', '👁️', '👀',
] as const;
const QUICK_REACTIONS = ['👍', '❤️', '😂', '🔥'] as const;
const CHAT_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
  '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥸', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️',
  '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓',
  '🤗', '🤔', '🫣', '🤭', '🫢', '🫡', '🤫', '🫠', '🤥', '😶', '😐', '😑', '😬', '🫥', '😯', '😦', '😧', '😮', '😲', '🥱',
  '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '👏', '🙌', '🙏', '🤝', '👊', '✊',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝',
  '🔥', '✨', '⭐', '🌟', '💫', '⚡', '💥', '💯',
  '🎉', '🎊', '🎈', '🎁', '🎂', '🍰', '🍕', '🍔', '🍟', '🍩', '🍪', '🍫',
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '✈️', '🚀',
  '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷',
  '🌍', '🌎', '🌏', '🌙', '🌞', '⭐', '☁️', '⛅', '🌈', '⚡', '❄️', '🔥', '👁️', '👀',
] as const;
const MAX_CONCURRENT_UPLOADS = 2;

// ── Upload queue ───────────────────────────────────────────────────────────────
interface UploadQueueItem {
  id: string;
  file: File;
  status: 'queued' | 'uploading' | 'done' | 'error';
  progress: number;
  errorMsg?: string;
}

function getSupportedMimeType(): string {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  return (
    types.find((t) => {
      try { return MediaRecorder.isTypeSupported(t); }
      catch { return false; }
    }) ?? ''
  );
}

// ── Image compression helper ──────────────────────────────────────────────────
function compressImage(file: File, maxPx: number, quality: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        let { naturalWidth: w, naturalHeight: h } = img;
        if (w > maxPx || h > maxPx) {
          if (w >= h) { h = Math.round(h * maxPx / w); w = maxPx; }
          else        { w = Math.round(w * maxPx / h); h = maxPx; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = ev.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface VideoRoomProps {
  partyId: string;
  username: string;
  isHost: boolean;
  partyName: string;
  onLeave: () => void;
  joinToken?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────
const VideoRoom: FC<VideoRoomProps> = ({
  partyId,
  username,
  isHost,
  partyName,
  onLeave,
  joinToken,
}) => {
  const normalizedPartyId = partyId?.toLowerCase() ?? '';

  // ── Party state ───────────────────────────────────────────────────────────
  const [localIsHost, setLocalIsHost] = useState<boolean>(isHost);
  const [users, setUsers] = useState<User[]>([]);
  const [notification, setNotification] = useState<AppNotification | null>(null);

  // ── Video state ───────────────────────────────────────────────────────────
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [currentVideoId, setCurrentVideoId] = useState<string | null>(null);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);

  // ── Player state ──────────────────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [volume, setVolume] = useState<number>(1);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // ── Chat state ────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageSeenBy, setMessageSeenBy] = useState<Record<string, string[]>>({});
  const [chatInput, setChatInput] = useState<string>('');
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const [mySocketId, setMySocketId] = useState<string>('');
  const [chatFocused, setChatFocused] = useState<boolean>(true);
  const [isFsChatOpen, setIsFsChatOpen] = useState<boolean>(true);
  const [fsChatPos, setFsChatPos] = useState<{ x: number; y: number }>({ x: 16, y: 16 });
  const [fsMsgHover, setFsMsgHover] = useState<string | null>(null);
  const [showFsChatEmoji, setShowFsChatEmoji] = useState<boolean>(false);
  const [fsControlsVisible, setFsControlsVisible] = useState<boolean>(true);

  // ── Activity & extras ─────────────────────────────────────────────────────
  const [activityLog, setActivityLog] = useState<ActivityItem[]>([]);
  const [floatingReactions, setFloatingReactions] = useState<FloatingReaction[]>([]);
  const [fullscreenBanner, setFullscreenBanner] = useState<FullscreenBanner | null>(null);
  const [reactionEmojis, setReactionEmojis] = useState<string[]>([...DEFAULT_REACTION_EMOJIS]);
  const [showReactionManager, setShowReactionManager] = useState<boolean>(false);
  const [messageReactions, setMessageReactions] = useState<MessageReactions>({});
  const [reactionToasts, setReactionToasts] = useState<ReactionToast[]>([]);
  const [msgPicker, setMsgPicker] = useState<string | null>(null);
  const [userAvatars, setUserAvatars] = useState<Record<string, string>>({});
  const [isDraggingImage, setIsDraggingImage] = useState<boolean>(false);

  // ── Mobile nav ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabType>('watch');

  // ── Refs ──────────────────────────────────────────────────────────────────
  const socketRef = useRef<Socket | null>(null);
  const usersRef = useRef<User[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLInputElement | null>(null);
  const emojiPickerRef = useRef<HTMLDivElement | null>(null);
  const reactionManagerRef = useRef<HTMLDivElement | null>(null);

  const isSyncingRef = useRef<boolean>(false);
  const isSeekingRef = useRef<boolean>(false);
  const seekValueRef = useRef<number>(0);
  const isRecordingRef = useRef<boolean>(false);
  const mySocketIdRef = useRef<string>('');
  const activeTabRef = useRef<TabType>(activeTab);
  const chatFocusedRef = useRef<boolean>(true);
  const isFullscreenRef = useRef<boolean>(false);
  const seenSentRef = useRef<Set<string>>(new Set());

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uploadingIdsRef = useRef<Set<string>>(new Set());
  const pickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fsChatDraggingRef = useRef<boolean>(false);
  const fsChatDragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const fsChatMessagesEndRef = useRef<HTMLDivElement | null>(null);
  const fsChatEmojiRef = useRef<HTMLDivElement | null>(null);
  const fsIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fsChatInputRef = useRef<HTMLInputElement | null>(null);
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const chatImageInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { activeTabRef.current = activeTab; }, [activeTab]);
  useEffect(() => { chatFocusedRef.current = chatFocused; }, [chatFocused]);
  useEffect(() => {
    const onDocClick = (ev: MouseEvent): void => {
      if (!showEmojiPicker) return;
      const target = ev.target as Node;
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showEmojiPicker]);
  useEffect(() => {
    const onDocClick = (ev: MouseEvent): void => {
      if (!showReactionManager) return;
      const target = ev.target as Node;
      if (reactionManagerRef.current && !reactionManagerRef.current.contains(target)) {
        setShowReactionManager(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showReactionManager]);

  useEffect(() => {
    const onDocClick = (ev: MouseEvent): void => {
      if (!showFsChatEmoji) return;
      const target = ev.target as Node;
      if (fsChatEmojiRef.current && !fsChatEmojiRef.current.contains(target)) {
        setShowFsChatEmoji(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showFsChatEmoji]);

  useEffect(() => {
    const onMove = (ev: MouseEvent | TouchEvent): void => {
      if (!fsChatDraggingRef.current) return;
      const point = 'touches' in ev ? ev.touches[0] : ev;
      if (!point) return;
      const width = 290;
      const height = 260;
      const maxX = Math.max(8, window.innerWidth - width - 8);
      const maxY = Math.max(8, window.innerHeight - height - 8);
      const nextX = Math.max(8, Math.min(point.clientX - fsChatDragOffsetRef.current.x, maxX));
      const nextY = Math.max(8, Math.min(point.clientY - fsChatDragOffsetRef.current.y, maxY));
      setFsChatPos({ x: nextX, y: nextY });
      if ('touches' in ev) ev.preventDefault();
    };
    const onUp = (): void => { fsChatDraggingRef.current = false; };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
    };
  }, []);

  const currentVideo = videos.find((v) => v.id === currentVideoId) ?? null;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const addActivity = useCallback((text: string): void => {
    setActivityLog((prev) =>
      [{ id: Date.now() + Math.random(), text }, ...prev].slice(0, 8),
    );
  }, []);

  const formatTime = (t: number): string => {
    if (isNaN(t)) return '0:00';
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatSize = (b: number): string =>
    b < 1_048_576
      ? `${(b / 1024).toFixed(0)} KB`
      : `${(b / 1_048_576).toFixed(1)} MB`;

  const formatTimestamp = (iso: string): string =>
    new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const fmtRecording = (s: number): string =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const volumeIcon: string =
    isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊';

  // ── Recording ─────────────────────────────────────────────────────────────
  const stopRecording = useCallback((): void => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (mediaRecorderRef.current && isRecordingRef.current) {
      try { mediaRecorderRef.current.stop(); } catch (_) { }
      isRecordingRef.current = false;
      setIsRecording(false);
      setRecordingTime(0);
    }
  }, []);

  const playNotificationSound = useCallback((): void => {
    try {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.35);
      setTimeout(() => ctx.close(), 700);
    } catch (_) { }
  }, []);

  const resetFsIdleTimer = useCallback((): void => {
    setFsControlsVisible(true);
    if (fsIdleTimerRef.current) clearTimeout(fsIdleTimerRef.current);
    fsIdleTimerRef.current = setTimeout(() => setFsControlsVisible(false), 3000);
  }, []);

  const handlePlayerMouseLeave = useCallback((): void => {
    if (isFullscreenRef.current) return; // fullscreen has no real "leave"
    if (fsIdleTimerRef.current) clearTimeout(fsIdleTimerRef.current);
    setFsControlsVisible(true);
  }, []);

  const startRecording = async (): Promise<void> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setNotification({ type: 'error', message: 'Audio recording not supported in this browser' });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];

      recorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (audioChunksRef.current.length === 0) return;
        const capturedVideoTime = videoRef.current?.currentTime ?? null;
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        if (blob.size > 6 * 1024 * 1024) {
          setNotification({ type: 'error', message: 'Voice message too large (max ~2 min)' });
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!socketRef.current?.connected) {
            setNotification({ type: 'error', message: 'Not connected — voice message not sent' });
            return;
          }
          socketRef.current.emit('chatMessage', {
            partyId: normalizedPartyId,
            message: { type: 'voice', audioData: reader.result as string, videoTime: capturedVideoTime },
          });
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      isRecordingRef.current = true;
      setIsRecording(true);
      setRecordingTime(0);

      recordTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= 119) { stopRecording(); return 0; }
          return prev + 1;
        });
      }, 1000);
    } catch (err) {
      const e = err as Error;
      const msg = e.name === 'NotAllowedError'
        ? 'Microphone access denied'
        : 'Could not start recording';
      setNotification({ type: 'error', message: msg });
    }
  };

  const toggleRecording = async (): Promise<void> => {
    if (isRecordingRef.current) stopRecording();
    else await startRecording();
  };

  // ── Socket setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!normalizedPartyId) {
      setNotification({ type: 'error', message: 'Invalid party ID' });
      return;
    }

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      withCredentials: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      mySocketIdRef.current = socket.id ?? '';
      setMySocketId(socket.id ?? '');
      socket.emit('joinParty', { partyId: normalizedPartyId, username, isHost, token: joinToken });
    });

    socket.on('partyJoined', ({ party }: { party: Party }) => {
      const allUsers = [party.host, ...(party.users ?? [])].filter(Boolean);
      setUsers(allUsers);
      usersRef.current = allUsers;
      if (party.videos) setVideos(party.videos);
      if (party.messages) setMessages(party.messages);
      if (party.currentVideoId) setCurrentVideoId(party.currentVideoId);
      if (party.playbackState) {
        setIsPlaying(party.playbackState.isPlaying);
        setPlaybackRate(party.playbackState.playbackRate ?? 1);
        setCurrentTime(party.playbackState.currentTime ?? 0);
      }
    });

    socket.on('userJoined', (user: User) => {
      setUsers((prev) => {
        if (prev.some((u) => u.id === user.id)) return prev;
        const updated = [...prev, user];
        usersRef.current = updated;
        return updated;
      });
      playNotificationSound();
      setNotification({ type: 'success', message: `${user.username} joined` });
      addActivity(`🙋 ${user.username} joined`);
    });

    socket.on('userLeft', (user: User) => {
      setUsers((prev) => {
        const updated = prev.filter((u) => u.id !== user.id);
        usersRef.current = updated;
        return updated;
      });
      playNotificationSound();
      setNotification({ type: 'info', message: `${user.username} left` });
      addActivity(`👋 ${user.username} left`);
    });

    socket.on('partyEnded', ({ message: msg }: { message: string }) => {
      setMessages([]);
      setMessageSeenBy({});
      seenSentRef.current.clear();
      setNotification({ type: 'error', message: msg, persistent: true });
      setTimeout(() => onLeave(), 3000);
    });

    socket.on('videoUploaded', ({ video }: { video: VideoItem }) => {
      setVideos((prev) => [...prev, video]);
      setNotification({ type: 'success', message: `New video: ${video.originalName}` });
    });

    socket.on('videoSelected', ({ videoId, username: who }: { videoId: string; username?: string }) => {
      setCurrentVideoId(videoId);
      setIsPlaying(false);
      setCurrentTime(0);
      if (who) addActivity(`🎬 ${who} selected a video`);
    });

    socket.on('playbackSync', ({ action, currentTime: syncTime, playbackRate: syncRate, username: who }: PlaybackSyncPayload) => {
      isSyncingRef.current = true;
      const vid = videoRef.current;
      if (!vid) { isSyncingRef.current = false; return; }

      if (action === 'play') {
        if (syncTime !== undefined && Math.abs(vid.currentTime - syncTime) > 0.5)
          vid.currentTime = syncTime;
        vid.play().catch(() => { });
        setIsPlaying(true);
        if (who) addActivity(`▶ ${who} played`);
      } else if (action === 'pause') {
        vid.pause();
        if (syncTime !== undefined && Math.abs(vid.currentTime - syncTime) > 0.5)
          vid.currentTime = syncTime;
        setIsPlaying(false);
        if (who) addActivity(`⏸ ${who} paused`);
      } else if (action === 'seek') {
        vid.currentTime = syncTime;
        setCurrentTime(syncTime);
        if (who) {
          const m = Math.floor(syncTime / 60);
          const s = Math.floor(syncTime % 60);
          addActivity(`⏩ ${who} → ${m}:${s.toString().padStart(2, '0')}`);
        }
      } else if (action === 'speed') {
        vid.playbackRate = syncRate ?? 1;
        setPlaybackRate(syncRate ?? 1);
        if (who) addActivity(`⚡ ${who} set ${syncRate}×`);
      }
      setTimeout(() => { isSyncingRef.current = false; }, 300);
    });

    socket.on('videoDeleted', ({ videoId }: { videoId: string }) => {
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
      setCurrentVideoId((prev) => (prev === videoId ? null : prev));
    });

    socket.on('newMessage', ({ message }: { message: Message }) => {
      setMessages((prev) => [...prev, message]);
      if (!document.hasFocus() && message.userId !== mySocketIdRef.current) {
        playNotificationSound();
      }
      const chatVisible = activeTabRef.current === 'chat' || chatFocusedRef.current;
      if (!chatVisible) setUnreadCount((prev) => prev + 1);
      if (chatVisible && message.userId !== mySocketIdRef.current) {
        markMessageSeen(message.id);
      }
      if (isFullscreenRef.current && message.userId !== mySocketIdRef.current) {
        const preview =
          message.type === 'voice'
            ? '🎙 Voice message'
            : (message.text ?? '').substring(0, 55);
        setFullscreenBanner({
          msgId: message.id,
          username: message.username,
          preview,
          videoTime: message.videoTime,
        });
        if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
        bannerTimerRef.current = setTimeout(() => setFullscreenBanner(null), 6000);
      }
    });

    socket.on('messageReaction', ({ messageId, emoji, userId, username: who }: MessageReactionPayload) => {
      setMessageReactions((prev) => {
        const msgReacts = { ...(prev[messageId] ?? {}) };
        const existing = msgReacts[emoji] ?? [];
        const already = existing.includes(userId);
        const updated = already
          ? existing.filter((id) => id !== userId)
          : [...existing, userId];
        if (updated.length === 0) delete msgReacts[emoji];
        else msgReacts[emoji] = updated;
        return { ...prev, [messageId]: msgReacts };
      });
      const toastId = Date.now() + Math.random();
      setReactionToasts((prev) => [...prev, { id: toastId, emoji, username: who }]);
      setTimeout(
        () => setReactionToasts((prev) => prev.filter((t) => t.id !== toastId)),
        2400,
      );
    });

    socket.on('reaction', (reaction: FloatingReactionPayload) => {
      const id = reaction.id;
      const left = 8 + Math.random() * 82;
      setFloatingReactions((prev) => [...prev, { ...reaction, id, left }]);
      setTimeout(
        () => setFloatingReactions((prev) => prev.filter((r) => r.id !== id)),
        2600,
      );
      addActivity(`${reaction.emoji} ${reaction.username} reacted`);
    });

    socket.on('messageSeen', ({ messageId, userId, username: who }: MessageSeenPayload) => {
      if (userId === mySocketIdRef.current) return;
      setMessageSeenBy((prev) => {
        const existing = prev[messageId] ?? [];
        if (existing.includes(who)) return prev;
        return { ...prev, [messageId]: [...existing, who] };
      });
    });

    socket.on('messageEdited', ({ messageId, text, editedAt }: MessageEditedPayload) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, text, editedAt } : m)),
      );
    });

    socket.on('userAvatarUpdated', ({ userId, avatar }: { userId: string; avatar: string | null }) => {
      setUserAvatars((prev) => {
        if (!avatar) { const next = { ...prev }; delete next[userId]; return next; }
        return { ...prev, [userId]: avatar };
      });
    });

    socket.on('hostTransferred', ({ newHost, leftUsername, leftUserId }: { newHost: User; leftUsername: string; leftUserId: string }) => {
      setUsers((prev) => {
        const updated = prev
          .filter((u) => u.id !== leftUserId)
          .map((u) => ({ ...u, isHost: u.id === newHost.id }));
        usersRef.current = updated;
        return updated;
      });
      playNotificationSound();
      addActivity(`👋 ${leftUsername} left the chat`);
      if (newHost.id === mySocketIdRef.current) {
        setLocalIsHost(true);
        setNotification({ type: 'success', message: `${leftUsername} left — you're now the host!` });
        addActivity('👑 You became the host');
      } else {
        setNotification({ type: 'info', message: `${leftUsername} left the chat` });
        addActivity(`👑 ${newHost.username} is now the host`);
      }
    });

    socket.on('error', ({ message: msg }: { message: string }) =>
      setNotification({ type: 'error', message: msg }),
    );
    socket.on('connect_error', () =>
      setNotification({ type: 'error', message: 'Failed to connect to server' }),
    );

    return () => {
      stopRecording();
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      if (pickerTimerRef.current) clearTimeout(pickerTimerRef.current); // eslint-disable-line react-hooks/exhaustive-deps
      if (fsIdleTimerRef.current) clearTimeout(fsIdleTimerRef.current);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedPartyId, username, isHost]);

  // ── Auto-dismiss notifications ────────────────────────────────────────────
  useEffect(() => {
    if (notification && !notification.persistent) {
      const t = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(t);
    }
  }, [notification]);

  // ── Reload video when selection changes ───────────────────────────────────
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
    // New video context should start clean: remove old chat/timeline artifacts.
    setMessages([]);
    setMessageReactions({});
    setMessageSeenBy({});
    setReactionToasts([]);
    setMsgPicker(null);
    setFullscreenBanner(null);
    setUnreadCount(0);
    setReplyToMessage(null);
    setEditingMessageId(null);
    seenSentRef.current.clear();
  }, [currentVideoId]);

  // ── Sync local volume ─────────────────────────────────────────────────────
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = isMuted ? 0 : volume;
      videoRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  // ── Auto-scroll chat ──────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    fsChatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Track fullscreen ──────────────────────────────────────────────────────
  useEffect(() => {
    const onFsChange = (): void => {
      const fs = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement
      );
      setIsFullscreen(fs);
      isFullscreenRef.current = fs;
      if (!fs) {
        setFullscreenBanner(null);
        if (fsIdleTimerRef.current) clearTimeout(fsIdleTimerRef.current);
        setFsControlsVisible(true);
      } else {
        resetFsIdleTimer();
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
    };
  }, [resetFsIdleTimer]);

  // ── Upload queue ──────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !localIsHost) return;
    const newItems: UploadQueueItem[] = files.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      status: 'queued' as const,
      progress: 0,
    }));
    setUploadQueue((prev) => [...prev, ...newItems]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const uploadFile = useCallback((item: UploadQueueItem): void => {
    if (uploadingIdsRef.current.has(item.id)) return;
    uploadingIdsRef.current.add(item.id);
    setUploadQueue((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, status: 'uploading' } : i)),
    );
    const formData = new FormData();
    formData.append('video', item.file);
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable)
        setUploadQueue((prev) =>
          prev.map((i) =>
            i.id === item.id
              ? { ...i, progress: Math.round((ev.loaded / ev.total) * 100) }
              : i,
          ),
        );
    };
    xhr.onload = () => {
      uploadingIdsRef.current.delete(item.id);
      setUploadQueue((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? xhr.status === 200
              ? { ...i, status: 'done', progress: 100 }
              : { ...i, status: 'error', errorMsg: `Server error (${xhr.status})` }
            : i,
        ),
      );
    };
    xhr.onerror = () => {
      uploadingIdsRef.current.delete(item.id);
      setUploadQueue((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, status: 'error', errorMsg: 'Network error' } : i,
        ),
      );
    };
    xhr.open('POST', `${API_BASE_URL}/api/party/${normalizedPartyId}/upload`);
    xhr.send(formData);
  }, [normalizedPartyId]);

  // Advance queue: start next queued item whenever queue state changes
  useEffect(() => {
    const inFlight = uploadingIdsRef.current.size;
    const availableSlots = Math.max(0, MAX_CONCURRENT_UPLOADS - inFlight);
    if (availableSlots === 0) return;
    const nextQueued = uploadQueue
      .filter((i) => i.status === 'queued' && !uploadingIdsRef.current.has(i.id))
      .slice(0, availableSlots);
    nextQueued.forEach((item) => uploadFile(item));
  }, [uploadQueue, uploadFile]);

  const retryUpload = (id: string): void => {
    setUploadQueue((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status: 'queued', progress: 0, errorMsg: undefined } : i)),
    );
  };

  const removeFromQueue = (id: string): void => {
    setUploadQueue((prev) => prev.filter((i) => i.id !== id));
  };

  // ── Playback ──────────────────────────────────────────────────────────────
  const handleSelectVideo = (videoId: string): void => {
    socketRef.current?.emit('selectVideo', { partyId: normalizedPartyId, videoId });
  };

  const handlePlay = (): void => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.play().catch(() => { });
    setIsPlaying(true);
    socketRef.current?.emit('playbackAction', {
      partyId: normalizedPartyId,
      action: 'play',
      currentTime: vid.currentTime,
    });
  };

  const handlePause = (): void => {
    const vid = videoRef.current;
    if (!vid) return;
    vid.pause();
    setIsPlaying(false);
    socketRef.current?.emit('playbackAction', {
      partyId: normalizedPartyId,
      action: 'pause',
      currentTime: vid.currentTime,
    });
  };

  const handleSeekStart = (): void => { isSeekingRef.current = true; };

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    seekValueRef.current = parseFloat(e.target.value);
    setCurrentTime(seekValueRef.current);
  };

  const handleSeekEnd = (): void => {
    const time = seekValueRef.current;
    isSeekingRef.current = false;
    if (videoRef.current) videoRef.current.currentTime = time;
    socketRef.current?.emit('playbackAction', {
      partyId: normalizedPartyId,
      action: 'seek',
      currentTime: time,
    });
  };

  const handleSpeedChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const rate = parseFloat(e.target.value);
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
    socketRef.current?.emit('playbackAction', {
      partyId: normalizedPartyId,
      action: 'speed',
      playbackRate: rate,
    });
  };

  const handleDeleteVideo = (e: React.MouseEvent, videoId: string): void => {
    e.stopPropagation();
    socketRef.current?.emit('deleteVideo', { partyId: normalizedPartyId, videoId });
  };

  // ── Volume (local only) ───────────────────────────────────────────────────
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    setIsMuted(val === 0);
  };

  const toggleMute = (): void => setIsMuted((prev) => !prev);

  // ── Fullscreen ────────────────────────────────────────────────────────────
  const toggleFullscreen = (): void => {
    const el = playerContainerRef.current;
    if (!el) return;
    const isFs = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement
    );
    if (isFs) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => { });
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    }
  };

  // ── Click: single = play/pause, double = fullscreen ───────────────────────
  const handlePlayerClick = (): void => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      toggleFullscreen();
    } else {
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        const vid = videoRef.current;
        if (!vid) return;
        if (vid.paused) handlePlay(); else handlePause();
      }, 220);
    }
  };

  const seekToTime = (time: number): void => {
    if (videoRef.current && time != null) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
      socketRef.current?.emit('playbackAction', {
        partyId: normalizedPartyId,
        action: 'seek',
        currentTime: time,
      });
    }
  };

  const scrollToMessage = (msgId: string): void => {
    setTimeout(() => {
      document
        .querySelector(`[data-msg-id="${msgId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
  };

  const markMessageSeen = useCallback((msgId: string): void => {
    if (!socketRef.current?.connected) return;
    if (seenSentRef.current.has(msgId)) return;
    seenSentRef.current.add(msgId);
    socketRef.current.emit('messageSeen', {
      partyId: normalizedPartyId,
      messageId: msgId,
    });
  }, [normalizedPartyId]);

  // ── Chat ──────────────────────────────────────────────────────────────────
  const sendTextMessage = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || !socketRef.current?.connected) return;
    if (editingMessageId) {
      socketRef.current.emit('editMessage', {
        partyId: normalizedPartyId,
        messageId: editingMessageId,
        text,
      });
      setEditingMessageId(null);
    } else {
      socketRef.current.emit('chatMessage', {
        partyId: normalizedPartyId,
        message: {
          type: 'text',
          text,
          videoTime: videoRef.current?.currentTime ?? null,
          replyTo: replyToMessage
            ? { id: replyToMessage.id }
            : undefined,
        },
      });
    }
    setChatInput('');
    setReplyToMessage(null);
    setShowEmojiPicker(false);
  };

  const handleEmojiInsert = (emoji: string): void => {
    setChatInput((prev) => `${prev}${emoji}`);
    setShowEmojiPicker(false);
    chatInputRef.current?.focus();
  };

  const onFsChatDragStart = (
    ev: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ): void => {
    ev.preventDefault();
    const point = 'touches' in ev ? ev.touches[0] : ev;
    fsChatDraggingRef.current = true;
    fsChatDragOffsetRef.current = {
      x: point.clientX - fsChatPos.x,
      y: point.clientY - fsChatPos.y,
    };
  };

  // ── Reactions ─────────────────────────────────────────────────────────────
  const handleSendReaction = (emoji: string): void => {
    socketRef.current?.emit('sendReaction', { partyId: normalizedPartyId, emoji });
  };

  const addReactionEmoji = (emoji: string): void => {
    setReactionEmojis((prev) => {
      if (prev.includes(emoji)) return prev;
      if (prev.length >= REACTION_EMOJI_LIMIT) return prev;
      return [...prev, emoji];
    });
  };

  const removeReactionEmoji = (emoji: string): void => {
    setReactionEmojis((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((e) => e !== emoji);
    });
  };

  const handleReactToMessage = (msgId: string, emoji: string): void => {
    socketRef.current?.emit('reactToMessage', {
      partyId: normalizedPartyId,
      messageId: msgId,
      emoji,
    });
  };

  const startReplyToMessage = (msg: Message): void => {
    setReplyToMessage(msg);
    setEditingMessageId(null);
    if (isFullscreenRef.current) fsChatInputRef.current?.focus();
    else chatInputRef.current?.focus();
  };

  const startEditMessage = (msg: Message): void => {
    if (msg.userId !== mySocketId || msg.type !== 'text') return;
    setEditingMessageId(msg.id);
    setReplyToMessage(null);
    setChatInput(msg.text ?? '');
    if (isFullscreenRef.current) fsChatInputRef.current?.focus();
    else chatInputRef.current?.focus();
  };

  const sendImageMessage = useCallback(async (file: File): Promise<void> => {
    if (!socketRef.current?.connected) return;
    try {
      const imageData = await compressImage(file, 1200, 0.75);
      socketRef.current.emit('chatMessage', {
        partyId: normalizedPartyId,
        message: { type: 'image', imageData, videoTime: videoRef.current?.currentTime ?? null },
      });
    } catch (_) {
      setNotification({ type: 'error', message: 'Failed to process image' });
    }
  }, [normalizedPartyId]);

  const handleAvatarUpload = useCallback(async (file: File): Promise<void> => {
    if (!socketRef.current?.connected) return;
    try {
      const avatar = await compressImage(file, 200, 0.8);
      socketRef.current.emit('setAvatar', { partyId: normalizedPartyId, avatar });
      setUserAvatars((prev) => ({ ...prev, [mySocketId]: avatar }));
    } catch (_) {
      setNotification({ type: 'error', message: 'Failed to process avatar' });
    }
  }, [normalizedPartyId, mySocketId]);

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const handleTabChange = (tab: TabType): void => {
    setActiveTab(tab);
    if (tab === 'chat') {
      setUnreadCount(0);
      setChatFocused(true);
      messages
        .filter((m) => m.userId !== mySocketIdRef.current)
        .forEach((m) => markMessageSeen(m.id));
    }
    else setChatFocused(false);
  };

  const handleChatFocus = (): void => {
    setChatFocused(true);
    setUnreadCount(0);
    messages
      .filter((m) => m.userId !== mySocketIdRef.current)
      .forEach((m) => markMessageSeen(m.id));
  };
  const handleChatBlur = (): void => setChatFocused(false);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="video-room" data-tab={activeTab}>

      {/* Header */}
      <div className="video-room-header">
        <div>
          <h2>{partyName}</h2>
          <p className="party-id">
            Party ID: <strong>{partyId}</strong>
            <button
              className="copy-btn"
              title="Copy invite link"
              onClick={() =>
                navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => setNotification({ type: 'success', message: 'Invite link copied!' }))
              }
            >
              📋
            </button>
          </p>
          <p className="user-count">
            {users.length} member{users.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="header-right">
          <div className="my-avatar-area">
            <button
              className="my-avatar-btn"
              onClick={() => avatarInputRef.current?.click()}
              title="Click to change your avatar"
            >
              {userAvatars[mySocketId]
                ? <img src={userAvatars[mySocketId]} alt="me" className="my-avatar-img" />
                : <span className="my-avatar-initial">{username.charAt(0).toUpperCase()}</span>
              }
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleAvatarUpload(file);
                if (avatarInputRef.current) avatarInputRef.current.value = '';
              }}
            />
          </div>
          <button
            className="btn btn-danger btn-small"
            onClick={() => {
              if (localIsHost) socketRef.current?.emit('leaveParty', { partyId: normalizedPartyId });
              onLeave();
            }}
          >
            Leave Party
          </button>
        </div>
      </div>

      {notification && (
        <div className={`notification ${notification.type}`}>{notification.message}</div>
      )}

      {/* Room layout */}
      <div className="room-layout">

        {/* Left sidebar */}
        <div className="sidebar">
          <div className="sidebar-section">
            <h3 className="sidebar-title">Members</h3>
            <ul className="members-list">
              {users.map((u) => (
                <li key={u.id} className={`member-item${u.isHost ? ' is-host' : ''}`}>
                  <span className="member-avatar">
                    {userAvatars[u.id]
                      ? <img src={userAvatars[u.id]} alt={u.username} className="member-avatar-img" />
                      : u.username.charAt(0).toUpperCase()
                    }
                  </span>
                  <span className="member-name">{u.username}</span>
                  {u.isHost && <span className="host-badge">HOST</span>}
                </li>
              ))}
            </ul>
            {activityLog.length > 0 && (
              <ul className="activity-log">
                {activityLog.map((a) => (
                  <li key={a.id} className="activity-item">{a.text}</li>
                ))}
              </ul>
            )}
          </div>

          {localIsHost && (
            <div className="sidebar-section">
              <h3 className="sidebar-title">Upload Video</h3>
              <div className="upload-area">
                <input
                  ref={fileInputRef}
                  id="video-upload"
                  type="file"
                  accept="video/*"
                  multiple
                  className="file-input"
                  onChange={handleFileChange}
                />
                <label htmlFor="video-upload" className="upload-label">
                  + Add Video
                </label>
                {uploadQueue.length > 0 && (
                  <ul className="upload-queue">
                    {uploadQueue.map((item, idx) => (
                      <li key={item.id} className={`uq-item uq-${item.status}`}>
                        <span className="uq-index">{idx + 1}</span>
                        <div className="uq-info">
                          <span className="uq-name">{item.file.name}</span>
                          <span className="uq-size">{formatSize(item.file.size)}</span>
                        </div>
                        <div className="uq-right">
                          {item.status === 'queued' && <span className="uq-badge">queued</span>}
                          {item.status === 'uploading' && <span className="uq-badge uq-badge--active">{item.progress}%</span>}
                          {item.status === 'done' && (
                            <>
                              <span className="uq-badge uq-badge--done">✓</span>
                              <button className="uq-dismiss" onClick={() => removeFromQueue(item.id)} title="Dismiss">×</button>
                            </>
                          )}
                          {item.status === 'error' && (
                            <>
                              <span className="uq-badge uq-badge--error" title={item.errorMsg}>✕</span>
                              <button className="uq-retry" onClick={() => retryUpload(item.id)} title="Retry">↺</button>
                            </>
                          )}
                        </div>
                        {item.status === 'uploading' && (
                          <div className="uq-progress">
                            <div className="uq-progress-fill" style={{ width: `${item.progress}%` }} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="sidebar-section">
            <h3 className="sidebar-title">
              Videos <span className="count-badge">{videos.length}</span>
            </h3>
            {videos.length === 0 ? (
              <p className="empty-text">
                {localIsHost ? 'No videos yet — upload one above.' : 'Waiting for host to upload…'}
              </p>
            ) : (
              <ul className="video-list">
                {videos.map((v) => (
                  <li
                    key={v.id}
                    className={`video-item${v.id === currentVideoId ? ' active' : ''}`}
                    onClick={() => handleSelectVideo(v.id)}
                  >
                    <div className="video-item-icon">▶</div>
                    <div className="video-item-info">
                      <span className="video-name">{v.originalName}</span>
                      <span className="video-meta">{formatSize(v.size)}</span>
                    </div>
                    {localIsHost && (
                      <button
                        className="delete-btn"
                        title="Delete"
                        onClick={(e) => handleDeleteVideo(e, v.id)}
                      >
                        ✕
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Center: player */}
        <div className="main-area">
          {!currentVideo ? (
            <div className="no-video-placeholder">
              <div className="placeholder-icon">🎬</div>
              <p>
                {localIsHost
                  ? 'Upload a video and select it to start watching.'
                  : 'Waiting for host to select a video…'}
              </p>
            </div>
          ) : (
            <div
              ref={playerContainerRef}
              className={`player-container${isFullscreen ? ' is-fullscreen' : ''}${fsControlsVisible ? ' controls-visible' : ' controls-hidden'}`}
              onMouseMove={resetFsIdleTimer}
              onMouseEnter={resetFsIdleTimer}
              onMouseLeave={handlePlayerMouseLeave}
            >
              {/* Floating reactions */}
              <div className="reactions-overlay" aria-hidden="true">
                {floatingReactions.map((r) => (
                  <div key={r.id} className="floating-reaction" style={{ left: `${r.left}%` }}>
                    <span className="floating-reaction-emoji">{r.emoji}</span>
                    <span className="floating-reaction-sender">
                      {userAvatars[r.userId]
                        ? <img src={userAvatars[r.userId]} alt={r.username} className="floating-reaction-avatar" />
                        : <span className="floating-reaction-initial">{r.username.charAt(0).toUpperCase()}</span>
                      }
                    </span>
                  </div>
                ))}
              </div>

              {/* Fullscreen message banner */}
              {fullscreenBanner && (
                <div
                  className="fs-banner"
                  onClick={() => {
                    toggleFullscreen();
                    handlePause();
                    if (fullscreenBanner.videoTime != null)
                      seekToTime(fullscreenBanner.videoTime);
                    scrollToMessage(fullscreenBanner.msgId);
                    setFullscreenBanner(null);
                  }}
                >
                  <span className="fs-banner-icon">💬</span>
                  <span className="fs-banner-text">
                    <strong>{fullscreenBanner.username}</strong>: {fullscreenBanner.preview}
                  </span>
                  {fullscreenBanner.videoTime != null && fullscreenBanner.videoTime > 0 && (
                    <span className="fs-banner-time">
                      ⏱ {formatTime(fullscreenBanner.videoTime)}
                    </span>
                  )}
                  <div
                    className="fs-banner-reactions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {QUICK_REACTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        className="fs-quick-react"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          // handleReactToMessage(fullscreenBanner.msgId, emoji);
                          setFullscreenBanner(null);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  <button
                    className="fs-banner-dismiss"
                    onClick={(e) => { e.stopPropagation(); setFullscreenBanner(null); }}
                  >
                    ×
                  </button>
                </div>
              )}

              {isFullscreen && (
                <div
                  className={`fs-chat-overlay${isFsChatOpen ? '' : ' minimized'}`}
                  style={{ left: fsChatPos.x, top: fsChatPos.y }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="fs-chat-header"
                    onMouseDown={onFsChatDragStart}
                    onTouchStart={onFsChatDragStart}
                  >
                    <span>💬 Chat</span>
                    <button
                      type="button"
                      className="fs-chat-toggle"
                      onClick={() => setIsFsChatOpen((prev) => !prev)}
                      title={isFsChatOpen ? 'Minimize chat' : 'Open chat'}
                    >
                      {isFsChatOpen ? '−' : '+'}
                    </button>
                  </div>
                  {isFsChatOpen && (
                    <>
                      <div className="fs-chat-messages">
                        {messages.length === 0 && (
                          <p className="fs-chat-empty">No messages yet</p>
                        )}
                        {messages.slice(-30).map((msg) => (
                          <div
                            key={`fs-${msg.id}`}
                            className={`fs-chat-msg${msg.userId === mySocketId ? ' own' : ''}`}
                            onMouseEnter={() => setFsMsgHover(msg.id)}
                            onMouseLeave={() => setFsMsgHover(null)}
                          >
                            {msg.userId !== mySocketId && (
                              <span className="fs-chat-author">{msg.username}</span>
                            )}
                            {msg.replyTo && (
                              <div className="fs-chat-reply-preview">
                                ↪ <strong>{msg.replyTo.username}</strong>: {msg.replyTo.textPreview}
                              </div>
                            )}
                            <div className="fs-chat-bubble">
                              {msg.type === 'voice' ? (
                                <span className="fs-chat-text">🎙 Voice message</span>
                              ) : msg.type === 'image' && msg.imageData ? (
                                <img src={msg.imageData} alt="Shared" className="msg-image" />
                              ) : (
                                <span className="fs-chat-text">
                                  {msg.text}
                                  {msg.editedAt && <span className="fs-chat-edited"> (edited)</span>}
                                </span>
                              )}
                            </div>
                            {fsMsgHover === msg.id && (
                              <div className={`fs-hover-reactions${msg.userId === mySocketId ? ' own' : ''}`}>
                                {QUICK_REACTIONS.map((emoji) => (
                                  <button
                                    key={emoji}
                                    type="button"
                                    className="fs-quick-react-sm"
                                    onClick={() => handleReactToMessage(msg.id, emoji)}
                                  >
                                    {emoji}
                                  </button>
                                ))}
                                <button
                                  type="button"
                                  className="fs-quick-react-sm"
                                  title="Reply"
                                  onClick={() => startReplyToMessage(msg)}
                                >
                                  ↩
                                </button>
                                {msg.userId === mySocketId && msg.type === 'text' && (
                                  <button
                                    type="button"
                                    className="fs-quick-react-sm"
                                    title="Edit"
                                    onClick={() => startEditMessage(msg)}
                                  >
                                    ✏
                                  </button>
                                )}
                              </div>
                            )}
                            <div className="fs-chat-meta">
                              {msg.videoTime != null && msg.videoTime > 0 && (
                                <button
                                  type="button"
                                  className="fs-chat-time-btn"
                                  onClick={() => seekToTime(msg.videoTime!)}
                                >
                                  ⏱ {formatTime(msg.videoTime)}
                                </button>
                              )}
                              {messageReactions[msg.id] &&
                                Object.keys(messageReactions[msg.id]).length > 0 && (
                                  <div className="fs-chat-reactions">
                                    {Object.entries(messageReactions[msg.id]).map(
                                      ([emoji, userIds]) =>
                                        userIds.length > 0 && (
                                          <button
                                            key={emoji}
                                            type="button"
                                            className="fs-chat-reaction-badge"
                                            onClick={() => handleReactToMessage(msg.id, emoji)}
                                          >
                                            {emoji} {userIds.length}
                                          </button>
                                        ),
                                    )}
                                  </div>
                                )}
                            </div>
                          </div>
                        ))}
                        <div ref={fsChatMessagesEndRef} />
                      </div>
                      {(replyToMessage || editingMessageId) && (
                        <div className="fs-chat-compose-state">
                          <span>
                            {replyToMessage ? `↩ ${replyToMessage.username}` : '✏ Editing'}
                          </span>
                          <button
                            type="button"
                            className="fs-chat-compose-cancel"
                            onClick={() => {
                              setReplyToMessage(null);
                              setEditingMessageId(null);
                              setChatInput('');
                            }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                      <form className="fs-chat-input-row" onSubmit={sendTextMessage}>
                        <div className="fs-emoji-wrap" ref={fsChatEmojiRef}>
                          <button
                            type="button"
                            className="fs-chat-icon-btn"
                            onClick={() => setShowFsChatEmoji((prev) => !prev)}
                            title="Emoji"
                          >
                            😊
                          </button>
                          {showFsChatEmoji && (
                            <div className="fs-emoji-popover">
                              {CHAT_EMOJIS.slice(0, 64).map((emoji) => (
                                <button
                                  key={emoji}
                                  type="button"
                                  className="fs-emoji-item"
                                  onClick={() => {
                                    setChatInput((prev) => `${prev}${emoji}`);
                                    setShowFsChatEmoji(false);
                                    fsChatInputRef.current?.focus();
                                  }}
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <input
                          ref={fsChatInputRef}
                          className="fs-chat-input"
                          type="text"
                          placeholder="Message…"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          maxLength={500}
                        />
                        <button
                          type="button"
                          className={`fs-chat-icon-btn${isRecording ? ' recording' : ''}`}
                          onClick={toggleRecording}
                          title={isRecording ? `Stop (${fmtRecording(recordingTime)})` : 'Voice message'}
                        >
                          {isRecording ? (
                            <span className="fs-rec-timer">{fmtRecording(recordingTime)}</span>
                          ) : '🎙'}
                        </button>
                        <button type="submit" className="fs-chat-send" disabled={!chatInput.trim()}>
                          ➤
                        </button>
                      </form>
                    </>
                  )}
                </div>
              )}

              {/* Video */}
              <div className="player-video-wrap" onClick={handlePlayerClick}>
                <video
                  ref={videoRef}
                  className={`video-player${isFullscreen ? ' video-player--fullscreen' : ''}`}
                  onTimeUpdate={() => {
                    if (!isSyncingRef.current && !isSeekingRef.current && videoRef.current)
                      setCurrentTime(videoRef.current.currentTime);
                  }}
                  onLoadedMetadata={() => {
                    if (videoRef.current) setDuration(videoRef.current.duration);
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                >
                  <source src={`${API_BASE_URL}${currentVideo.url}`} />
                </video>
                {!isPlaying && (
                  <div className="play-overlay-hint" aria-hidden="true">▶</div>
                )}
              </div>

              {/* Controls */}
              <div className="player-controls">
                <button
                  className="ctrl-btn play-pause-btn"
                  onClick={isPlaying ? handlePause : handlePlay}
                >
                  {isPlaying ? '⏸' : '▶'}
                </button>
                <span className="time-label">{formatTime(currentTime)}</span>
                <div className="seek-wrap">
                  <input
                    type="range"
                    className="seek-bar"
                    min="0"
                    max={duration || 0}
                    step="0.5"
                    value={currentTime}
                    onMouseDown={handleSeekStart}
                    onChange={handleSeekChange}
                    onMouseUp={handleSeekEnd}
                    onTouchStart={handleSeekStart}
                    onTouchEnd={handleSeekEnd}
                  />
                  {duration > 0 &&
                    messages
                      .filter((m) => (m.videoTime ?? 0) > 0)
                      .map((m) => (
                        <div
                          key={m.id}
                          className="seek-marker"
                          style={{ left: `${((m.videoTime ?? 0) / duration) * 100}%` }}
                          title={`${m.username}: ${m.type === 'voice'
                            ? '🎙 Voice'
                            : (m.text ?? '').substring(0, 40)
                            } @ ${formatTime(m.videoTime ?? 0)}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            seekToTime(m.videoTime ?? 0);
                          }}
                        />
                      ))}
                </div>
                <span className="time-label">{formatTime(duration)}</span>
                <button className="ctrl-btn" onClick={toggleMute} title="Toggle mute">
                  {volumeIcon}
                </button>
                <input
                  type="range"
                  className="volume-bar"
                  min="0"
                  max="1"
                  step="0.05"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  title="Volume (local only — not synced)"
                />
                <select
                  className="speed-select"
                  value={playbackRate}
                  onChange={handleSpeedChange}
                >
                  <option value="0.5">0.5×</option>
                  <option value="1">1×</option>
                  <option value="1.25">1.25×</option>
                  <option value="1.5">1.5×</option>
                  <option value="2">2×</option>
                </select>
                <button
                  className="ctrl-btn"
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? '⊡' : '⛶'}
                </button>
              </div>

              {/* Reactions bar */}
              <div className="reactions-bar">
                {reactionEmojis.map((emoji) => (
                  <button
                    key={emoji}
                    className="reaction-btn"
                    onClick={() => handleSendReaction(emoji)}
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
                <div className="reaction-manager-wrap" ref={reactionManagerRef}>
                  <button
                    className="reaction-manage-btn"
                    onClick={() => setShowReactionManager((prev) => !prev)}
                    title="Customize reactions"
                  >
                    +
                  </button>
                  {showReactionManager && (
                    <div className="reaction-manager-popover">
                      <div className="reaction-manager-title">
                        Reactions ({reactionEmojis.length}/{REACTION_EMOJI_LIMIT})
                      </div>
                      <div className="reaction-selected-list">
                        {reactionEmojis.map((emoji) => (
                          <button
                            key={`selected-${emoji}`}
                            className="reaction-selected-item"
                            onClick={() => removeReactionEmoji(emoji)}
                            title={
                              reactionEmojis.length <= 1
                                ? 'At least one reaction is required'
                                : 'Remove from reaction bar'
                            }
                            disabled={reactionEmojis.length <= 1}
                          >
                            <span>{emoji}</span>
                            <span className="reaction-selected-remove">×</span>
                          </button>
                        ))}
                      </div>
                      <div className="reaction-manager-subtitle">Add more</div>
                      <div className="reaction-catalog-list">
                        {REACTION_EMOJI_CATALOG
                          .filter((emoji) => !reactionEmojis.includes(emoji))
                          .map((emoji) => (
                            <button
                              key={`catalog-${emoji}`}
                              className="reaction-catalog-item"
                              onClick={() => addReactionEmoji(emoji)}
                              disabled={reactionEmojis.length >= REACTION_EMOJI_LIMIT}
                              title={
                                reactionEmojis.length >= REACTION_EMOJI_LIMIT
                                  ? `Limit ${REACTION_EMOJI_LIMIT} reached — remove one first`
                                  : 'Add to reaction bar'
                              }
                            >
                              {emoji}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: chat */}
        <div
          className={`chat-panel${isDraggingImage ? ' drag-over' : ''}`}
          onFocus={handleChatFocus}
          onBlur={handleChatBlur}
          onDragOver={(e) => { e.preventDefault(); setIsDraggingImage(true); }}
          onDragLeave={() => setIsDraggingImage(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingImage(false);
            const file = e.dataTransfer.files[0];
            if (file && file.type.startsWith('image/')) sendImageMessage(file);
          }}
        >
          <div className="chat-header">
            <span className="sidebar-title" style={{ margin: 0 }}>
              💬 Chat
              {unreadCount > 0 && (
                <span className="chat-unread-badge">{unreadCount}</span>
              )}
            </span>
          </div>

          <div className="chat-messages" onClick={handleChatFocus}>
            {messages.length === 0 && (
              <p className="empty-text" style={{ textAlign: 'center', paddingTop: 28 }}>
                No messages yet.<br />Say hello! 👋
              </p>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                data-msg-id={msg.id}
                className={`chat-msg${msg.userId === mySocketId ? ' own' : ''}`}
                onMouseEnter={() => setMsgPicker(msg.id)}
                onMouseLeave={() => setMsgPicker(null)}
              >
                {msg.userId !== mySocketId && (
                  <span className="msg-author">{msg.username}</span>
                )}

                <div className="msg-content-wrap">
                  {msg.replyTo && (
                    <div className="msg-reply-preview">
                      ↪ <strong>{msg.replyTo.username}</strong>: {msg.replyTo.textPreview}
                    </div>
                  )}
                  {msg.type === 'text' && (
                    <div className="msg-bubble">
                      {msg.text}
                      {msg.editedAt && <span className="msg-edited"> (edited)</span>}
                    </div>
                  )}
                  {msg.type === 'voice' && msg.audioData && (
                    <VoiceMessage
                      audioData={msg.audioData}
                      isOwn={msg.userId === mySocketId}
                    />
                  )}
                  {msg.type === 'image' && msg.imageData && (
                    <div className="msg-image-wrap">
                      <img src={msg.imageData} alt="Shared" className="msg-image" />
                    </div>
                  )}
                </div>

                {messageReactions[msg.id] &&
                  Object.keys(messageReactions[msg.id]).length > 0 && (
                    <div className="msg-reactions">
                      {Object.entries(messageReactions[msg.id]).map(
                        ([emoji, userIds]) =>
                          userIds.length > 0 && (
                            <button
                              key={emoji}
                              className="msg-reaction-badge"
                              onClick={() => handleReactToMessage(msg.id, emoji)}
                              title="Click to add/remove"
                            >
                              {emoji} {userIds.length}
                            </button>
                          ),
                      )}
                    </div>
                  )}

                {msgPicker === msg.id && (
                  <div className="msg-inline-reactions">
                    {QUICK_REACTIONS.map((emoji) => (
                      <button
                        key={emoji}
                        className="quick-react-btn"
                        onClick={() => {
                          handleReactToMessage(msg.id, emoji);
                          setMsgPicker(null);
                        }}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}

                <div className="msg-meta-row">
                  <span className="msg-time">{formatTimestamp(msg.timestamp)}</span>
                  <button
                    className="msg-action"
                    onClick={() => startReplyToMessage(msg)}
                    title="Reply"
                  >
                    Reply
                  </button>
                  {msg.userId === mySocketId && msg.type === 'text' && (
                    <button
                      className="msg-action"
                      onClick={() => startEditMessage(msg)}
                      title="Edit message"
                    >
                      Edit
                    </button>
                  )}
                  {msg.userId === mySocketId && (messageSeenBy[msg.id]?.length ?? 0) > 0 && (
                    <span className="msg-seen">Seen by {messageSeenBy[msg.id].join(', ')}</span>
                  )}
                  {msg.videoTime != null && msg.videoTime > 0 && (
                    <button
                      className="msg-videotime"
                      onClick={(e) => { e.stopPropagation(); seekToTime(msg.videoTime!); }}
                      title="Jump to this moment"
                    >
                      ⏱ {formatTime(msg.videoTime)}
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
            {reactionToasts.length > 0 && (
              <div className="reaction-toasts" aria-hidden="true">
                {reactionToasts.map((t) => (
                  <div key={t.id} className="reaction-toast">
                    {t.emoji} <strong>{t.username}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>

          <form className="chat-input-area" onSubmit={sendTextMessage}>
            {(replyToMessage || editingMessageId) && (
              <div className="chat-compose-state">
                {replyToMessage && (
                  <span>
                    Replying to <strong>{replyToMessage.username}</strong>
                  </span>
                )}
                {editingMessageId && <span>Editing message</span>}
                <button
                  type="button"
                  className="chat-compose-cancel"
                  onClick={() => {
                    setReplyToMessage(null);
                    setEditingMessageId(null);
                    setChatInput('');
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            <div className="emoji-picker-wrap" ref={emojiPickerRef}>
              <button
                type="button"
                className={`emoji-toggle-btn${showEmojiPicker ? ' active' : ''}`}
                onClick={() => setShowEmojiPicker((prev) => !prev)}
                title="Add emoji"
              >
                😊
              </button>
              {showEmojiPicker && (
                <div className="emoji-popover">
                  {CHAT_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="emoji-item-btn"
                      onClick={() => handleEmojiInsert(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input
              ref={chatInputRef}
              className="chat-input"
              type="text"
              placeholder="Message…"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onFocus={handleChatFocus}
              maxLength={500}
            />
            <button
              type="button"
              className="img-upload-btn"
              onClick={() => chatImageInputRef.current?.click()}
              title="Share image"
            >
              🖼
            </button>
            <input
              ref={chatImageInputRef}
              type="file"
              accept="image/*"
              className="hidden-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) sendImageMessage(file);
                if (chatImageInputRef.current) chatImageInputRef.current.value = '';
              }}
            />
            <button
              type="button"
              className={`mic-btn${isRecording ? ' recording' : ''}`}
              onClick={toggleRecording}
              title={
                isRecording
                  ? `Stop recording (${fmtRecording(recordingTime)})`
                  : 'Record voice message'
              }
            >
              {isRecording
                ? <span className="rec-timer">{fmtRecording(recordingTime)}</span>
                : '🎙'}
            </button>
            <button type="submit" className="send-btn" disabled={!chatInput.trim()}>
              ➤
            </button>
          </form>
        </div>
      </div>

      {/* Mobile bottom tabs */}
      <nav className="bottom-tabs">
        <button
          className={`tab-btn${activeTab === 'watch' ? ' active' : ''}`}
          onClick={() => handleTabChange('watch')}
        >
          <span className="tab-icon">▶</span>
          <span className="tab-label">Watch</span>
        </button>
        <button
          className={`tab-btn${activeTab === 'chat' ? ' active' : ''}`}
          onClick={() => handleTabChange('chat')}
        >
          <span className="tab-icon-wrap">
            💬
            {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
          </span>
          <span className="tab-label">Chat</span>
        </button>
        <button
          className={`tab-btn${activeTab === 'party' ? ' active' : ''}`}
          onClick={() => handleTabChange('party')}
        >
          <span className="tab-icon">👥</span>
          <span className="tab-label">Party</span>
        </button>
      </nav>

    </div>
  );
};

export default VideoRoom;
